import produce from 'immer'
import _flatten from 'lodash/flatten'
import {
  createContext,
  Dispatch,
  FunctionComponent,
  ReactNode,
  useContext,
  useReducer
} from 'react'

const MARKER_ACTION_TYPE = 'show_timeline_marker'
const OVERLAY_SHOW_ACTION_TYPE = 'show_overlay'
const OVERLAY_HIDE_ACTION_TYPE = 'hide_overlay'
const OVERLAY_RESHOW_ACTION_TYPE = 'reshow_overlay'
const DELETE_ACTION_TYPE = 'delete_action'

export type actionType =
  | 'show_timeline_marker'
  | 'show_overlay'
  | 'hide_overlay'
  | 'reshow_overlay'
  | 'set_variable'
  | 'increment_variable'
  | 'create_timer'
  | 'start_timer'
  | 'pause_timer'
  | 'adjust_timer'
  | 'skip_timer'
  | 'delete_action'
  | 'show_midroll'
export type animateinType =
  | 'fade_in'
  | 'slide_from_top'
  | 'slide_from_bottom'
  | 'slide_from_left'
  | 'slide_from_right'
  | 'none'
export type animateoutType =
  | 'fade_out'
  | 'slide_to_top'
  | 'slide_to_bottom'
  | 'slide_to_left'
  | 'slide_to_right'
  | 'none'

interface ActionCallBase {
  id: string
  type: actionType
  offset: number
}

export interface OverlayPosition {
  bottom?: number
  top?: number
  vcenter?: number
  left?: number
  right?: number
  hcenter?: number
}

// Overlay
export type OverlayActionCall =
  | ShowOverlayActionCall
  | HideOverlayActionCall
  | ReshowOverlayActionCall

export interface ShowOverlayActionCall extends ActionCallBase {
  type: 'show_overlay'
  data: ShowOverlayActionCallData
}

interface HideOverlayActionCall extends ActionCallBase {
  type: 'hide_overlay'
  data: {
    custom_id: string
    animateout_duration: number
    animateout_type: animateoutType
  }
}

interface ReshowOverlayActionCall extends ActionCallBase {
  type: 'reshow_overlay'
  data: {
    custom_id: string
  }
}

export interface ShowOverlayActionCallData {
  custom_id: string
  svg_url?: string
  position: OverlayPosition
  size:
    | {
        height: number
        width?: number
      }
    | {
        height?: number
        width: number
      }
    | {
        height: number
        width: number
      }
  animatein_type?: animateinType
  animateout_type?: animateoutType
  animatein_duration?: number
  animateout_duration?: number
  duration?: number
  repeat_interval?: number
  opacity?: number
  delay?: number
  variables?: string[]
  ad_data?: AdOverlayActionCallData
}

export interface ShowOverlayActionCallData {
  custom_id: string
  animateout_type?: animateoutType
  animateout_duration?: number
  variables?: string[]
}

export interface AdOverlayActionCallData {
  ad_unit_path: string
  ad_targeting?: Record<string, string | string[]>
  ad_slot_refresh_rate?: number
}

// Marker
export interface MarkerActionCall extends ActionCallBase {
  type: 'show_timeline_marker'
  data: MarkerActionCallData
}

export interface MarkerActionCallData {
  color: string
  label: string
  seek_offset?: number
}

// Delete Action Call
export interface DeleteActionCall extends ActionCallBase {
  type: 'delete_action'
  data: DeleteActionData
}

export interface DeleteActionData {
  action_id: string
}

// Midroll
export interface MidrollActionCall extends ActionCallBase {
  type: 'show_midroll'
  data: MidrollActionData
}

interface MidrollActionData {
  tag: string
  prefetch_offset?: number
}

// Variable

export type VariableActionCall =
  | IncrementVariableActionCall
  | DecrementVariableActionCall

export interface IncrementVariableActionCall extends ActionCallBase {
  type: 'set_variable'
  data: SetVariableActionData
}

export interface DecrementVariableActionCall extends ActionCallBase {
  type: 'increment_variable'
  data: {
    name: string
    amount: number
  }
}

interface SetVariableActionData {
  name: string
  type: 'double' | 'long' | 'string'
  value: string | number
  double_precision?: number
}

// Timer
export interface CreateTimerActionCall extends ActionCallBase {
  type: 'create_timer'
  data: TimerActionCallData
}

interface StartTimerActionCall extends ActionCallBase {
  type: 'start_timer'
  data: {
    name: string
  }
}

interface PauseTimerActionCall extends ActionCallBase {
  type: 'pause_timer'
  data: {
    name: string
  }
}

interface AdjustTimerActionCall extends ActionCallBase {
  type: 'adjust_timer'
  data: {
    name: string
    value: number
  }
}

interface SkipTimerActionCall extends ActionCallBase {
  type: 'skip_timer'
  data: {
    name: string
    value: number
  }
}

export type TimerActionCall =
  | CreateTimerActionCall
  | StartTimerActionCall
  | PauseTimerActionCall
  | AdjustTimerActionCall
  | SkipTimerActionCall

interface TimerActionCallData {
  name: string
  format?: TimeType
  direction?: 'up' | 'down'
  start_value?: number
  step?: number
  cap_value?: number
  value?: number
}
export type TimeType = 'ms' | 's'

export type ActionCall =
  | OverlayActionCall
  | MarkerActionCall
  | VariableActionCall
  | TimerActionCall
  | DeleteActionCall
  | MidrollActionCall

export type AnnotationsFetcher = ({
  id,
}: {
  id: string
}) => Promise<{ data: { actions: ActionCall[] }; errors: never }>

export interface AnnotationsStore {
  timeoutId: number
  currentId: string
  actions: ActionCall[]
  preloadedImages: { [key: string]: string }
  cancelledActions: string[]
  markers: MarkerActionCall[]
  overlays: OverlayActionCall[]
  variables: VariableActionCall[]
  timers: TimerActionCall[]
}

export type StoreAction =
  | { type: 'setActions'; actions: ActionCall[] }
  | { type: 'dispose' }
  | { type: 'preloadOverlayImage'; overlayID: string; contents: string }

const initialStore: AnnotationsStore = {
  timeoutId: 0,
  currentId: '',
  actions: [],
  preloadedImages: {},
  cancelledActions: [],
  markers: [],
  overlays: [],
  variables: [],
  timers: [],
}

const reducer = produce((draft: AnnotationsStore, action: StoreAction) => {
  switch (action.type) {
    case 'setActions': {
      if (draft.actions.length === action.actions.length) break

      draft.actions = action.actions
      draft.cancelledActions = (
        action.actions.filter(
          (aaction: ActionCall) =>
            'action_id' in aaction.data && aaction.type === DELETE_ACTION_TYPE,
        ) as DeleteActionCall[]
      ).map((aaction: DeleteActionCall) => aaction.data.action_id)
      draft.overlays = _flatten(
        draft.actions
          .filter(
            (aaction: ActionCall) =>
              [
                OVERLAY_SHOW_ACTION_TYPE,
                OVERLAY_HIDE_ACTION_TYPE,
                OVERLAY_RESHOW_ACTION_TYPE,
              ].includes(aaction.type) &&
              !draft.cancelledActions.includes(aaction.id),
          )
          .sort(sortActionsByPriorities),
      ) as OverlayActionCall[]
      draft.markers = _flatten(
        draft.actions.filter(
          (aaction: ActionCall) =>
            aaction.type === MARKER_ACTION_TYPE &&
            aaction.offset > 0 &&
            !draft.cancelledActions.includes(aaction.id),
        ) as MarkerActionCall[],
      )
      draft.variables = draft.actions
        .filter(
          (aaction: ActionCall) =>
            aaction.type === 'set_variable' ||
            aaction.type === 'increment_variable',
        )
        .sort(sortActionsByPriorities) as VariableActionCall[]
      draft.timers = draft.actions.filter((aaction: ActionCall) =>
        [
          'create_timer',
          'start_timer',
          'pause_timer',
          'adjust_timer',
          'skip_timer',
        ].includes(aaction.type),
      ).sort(sortActionsByPriorities) as TimerActionCall[]
      break
    }
    case 'dispose': {
      draft = { ...initialStore }
      break
    }
    case 'preloadOverlayImage': {
      draft.preloadedImages[action.overlayID] = action.contents
    }
  }
})

export const AnnotationsContext = createContext({
  state: initialStore,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,  @typescript-eslint/no-empty-function
  dispatch: (_a: StoreAction) => {},
})

export const AnnotationsContextProvider: FunctionComponent<{
  children: ReactNode
}> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialStore)

  return (
    <AnnotationsContext.Provider value={{ state, dispatch }}>
      {children}
    </AnnotationsContext.Provider>
  )
}

export function useAnnotationsStore(): AnnotationsStore {
  const ctx = useContext(AnnotationsContext)
  return ctx.state
}

export function useDispatch(): Dispatch<StoreAction> {
  const ctx = useContext(AnnotationsContext)
  return ctx.dispatch
}

export function useVariables(): ActionCall[] {
  const ctx = useContext(AnnotationsContext)
  return ctx.state.variables
}

export function useTimers(): ActionCall[] {
  const ctx = useContext(AnnotationsContext)
  return ctx.state.timers
}

export function useMarkers(): MarkerActionCall[] {
  const ctx = useContext(AnnotationsContext)
  return ctx.state.markers
}

export function useOverlays(): OverlayActionCall[] {
  const ctx = useContext(AnnotationsContext)
  return ctx.state.overlays
}

const actionPriorities: Record<string, number> = {
  set_variable: 1000,
  create_timer: 1000,
  increment_variable: 500,
  start_timer: 500,
  pause_timer: 400,
  adjust_timer: 300,
  skip_timer: 200,
  show_overlay: 100,
  hide_overlay: 50,
  reshow_overlay: 40,
}

export function sortActionsByPriorities(
  a: { offset: number; type: string },
  b: { offset: number; type: string },
): number {
  if (a.offset === b.offset) {
    if (actionPriorities[a.type] < actionPriorities[b.type]) {
      return 1
    } else if (actionPriorities[a.type] > actionPriorities[b.type]) {
      return -1
    }

    return 0
  }

  return a.offset - b.offset
}
